import type { WorkwiseProduct } from '~/entities/Requests/api/workwise/types';

/** Platform discriminator returned by the unified access-request endpoints. */
export type RequestPlatform = 'workday' | 'workwise';

export function toRequestPlatform(
  productKey?: string | null
): RequestPlatform | undefined {
  return productKey === 'workday' || productKey === 'workwise'
    ? productKey
    : undefined;
}

export interface RequestProduct {
  key: string;
  /** Brand name — never translated (e.g. Workday, WorkWise). */
  displayName: string;
  /** Menu/page title; product name stays literal. */
  title: string;
  enabled?: boolean;
}

export const REQUEST_PRODUCTS: RequestProduct[] = [
  {
    key: 'workday',
    displayName: 'Workday',
    title: 'Workday Request',
    enabled: true
  },
  {
    key: 'workwise',
    displayName: 'WorkWise',
    title: 'WorkWise Request',
    enabled: true
  }
];

export function getProductDisplayName(productKey: string): string {
  return (
    REQUEST_PRODUCTS.find(product => product.key === productKey)?.displayName ??
    productKey
  );
}

const KNOWN_REQUEST_PRODUCT_KEYS = new Set(
  REQUEST_PRODUCTS.map(product => product.key)
);

export function isActiveRequestProduct(
  product: Pick<WorkwiseProduct, 'name' | 'enabled'>
) {
  if (!KNOWN_REQUEST_PRODUCT_KEYS.has(product.name)) {
    return false;
  }

  const staticProduct = REQUEST_PRODUCTS.find(
    requestProduct => requestProduct.key === product.name
  );
  if (staticProduct?.enabled === false) {
    return false;
  }

  return product.enabled !== false;
}

export function resolveEnabledRequestProducts(
  apiProducts?: WorkwiseProduct[]
): RequestProduct[] {
  if (apiProducts !== undefined) {
    const activeProductKeys = new Set(
      apiProducts.filter(isActiveRequestProduct).map(product => product.name)
    );

    return REQUEST_PRODUCTS.filter(product =>
      activeProductKeys.has(product.key)
    );
  }

  return REQUEST_PRODUCTS.filter(product => product.enabled !== false);
}

export function getDefaultRequestProductKey(
  apiProducts?: WorkwiseProduct[]
): string | undefined {
  return resolveEnabledRequestProducts(apiProducts)[0]?.key;
}

export function isRequestProductEnabled(
  productKey: string,
  apiProducts?: WorkwiseProduct[]
) {
  return resolveEnabledRequestProducts(apiProducts).some(
    product => product.key === productKey
  );
}
